/**
 * 結果タブ: 群間比較 / 統計詳細 / 論文用（横並び）
 * 中央ペイン下部に配置。タブバーは自前で flex row にし確実に横並びにする。
 */

import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Button, Accordion, Spinner } from 'react-bootstrap';
import { useKeisukeDashboardStore } from '../../../../stores/systems/keisuke/dashboardStore';
import type { GroupComparisonRow, ResultTabKey } from '../../../../stores/systems/keisuke/dashboardStore';
import CalculationDetailsContent from './CalculationDetailsContent';
import CalculationDetailsModal from './CalculationDetailsModal';
import styles from './KeisukeDashboardResultTabs.module.scss';
import { downloadKeisukeDashboardFullReportPdf } from '../../../../services/systems/keisuke/keisukeReportPdf';
import {
  PAPER_TEXT_LOCALES,
  normalizePaperTextLocale,
  resolvePaperText,
  type PaperTextLocale,
} from '../../../../utils/keisukePaperText';
import { tTestComparisonTitleKey, tTestMethodShortLabelKey } from '../../../../utils/keisukeTTestDisplay';
import { pValueToSymbol } from './keisukeDashboardChartUtils';
import {
  formatPValueCell,
  formatStatKeyLabel,
  formatStatValue,
  assumptionHintMessageKey,
  filterCoreStatsBeyondGlance,
  partitionStatEntries,
} from './keisukeDashboardStatLabels';

const RESULT_TAB_KEYS: { key: ResultTabKey; labelKey: string }[] = [
  { key: 'comparison', labelKey: 'keisuke.dashboard.resultTab.comparison' },
  { key: 'stats', labelKey: 'keisuke.dashboard.resultTab.stats' },
  { key: 'paper', labelKey: 'keisuke.dashboard.resultTab.paper' },
];

function getTabLabel(key: ResultTabKey, t: (k: string) => string): string {
  const found = RESULT_TAB_KEYS.find((row) => row.key === key);
  return found ? t(found.labelKey) : key;
}

function ComparisonKeyValueTable({
  rows,
  t,
  groupNames,
}: {
  rows: Array<[string, unknown]>;
  t: (key: string, opts?: { defaultValue?: string }) => string;
  groupNames?: { a: string; b: string };
}) {
  if (rows.length === 0) return null;
  return (
    <table className={styles.comparisonKeyStatsTable}>
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <th scope="row">{formatStatKeyLabel(key, t, groupNames)}</th>
            <td>{formatStatValue(key, value, t)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// グラフの有意記号(*, **, ***)と揃え、非有意は ns（Bonferroni 等の significant フラグを尊重）
function formatComparisonSignificance(
  p: number,
  significant: boolean,
  nsLabel: string
): string {
  if (!significant) return nsLabel;
  return pValueToSymbol(p) || '*';
}

function GroupComparisonTable({
  rows,
  t,
  postHocKind,
  colAHeader,
  colBHeader,
}: {
  rows: GroupComparisonRow[];
  t: (key: string, opts?: { defaultValue?: string }) => string;
  postHocKind: string;
  /** 省略時は ANOVA 用「群A/群B」。対応あり t検定は「前/後」を渡す */
  colAHeader?: string;
  colBHeader?: string;
}) {
  return (
    <table className="table table-sm table-bordered mb-0">
      <thead>
        <tr>
          <th>{colAHeader ?? t('keisuke.dashboard.anovaPairwiseGroupA')}</th>
          <th>{colBHeader ?? t('keisuke.dashboard.anovaPairwiseGroupB')}</th>
          <th>
            {postHocKind === 'bonferroni'
              ? t('keisuke.dashboard.anovaPairwisePValueBonferroni')
              : t('keisuke.dashboard.anovaPairwisePValue')}
          </th>
          <th>{t('keisuke.dashboard.anovaPairwiseSignificant')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={`${row.groupA}-${row.groupB}-${idx}`}>
            <td>{row.groupA}</td>
            <td>{row.groupB}</td>
            <td>{formatPValueCell(row.pValue)}</td>
            <td className={styles.significanceMark} title={formatPValueCell(row.pValue)}>
              {formatComparisonSignificance(
                row.pValue,
                row.significant,
                t('keisuke.dashboard.significanceNs')
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 単一ペアの t 検定: 「群A/群B」見出しと実群名の二重表記を避ける */
function TTestComparisonStrip({
  row,
  t,
  postHocKind,
}: {
  row: GroupComparisonRow;
  t: (key: string, opts?: { defaultValue?: string }) => string;
  postHocKind: string;
}) {
  const pLabel =
    postHocKind === 'bonferroni'
      ? t('keisuke.dashboard.anovaPairwisePValueBonferroni')
      : t('keisuke.dashboard.anovaPairwisePValue');
  const pText = formatPValueCell(row.pValue);
  const sig = formatComparisonSignificance(
    row.pValue,
    row.significant,
    t('keisuke.dashboard.significanceNs')
  );
  return (
    <div
      className={styles.ttestComparisonStrip}
      data-keisuke-ttest-comparison-strip
      role="group"
      aria-label={`${row.groupA} vs ${row.groupB}, ${pLabel} ${pText}, ${sig}`}
    >
      <span className={styles.ttestComparisonPair}>
        <span className={styles.ttestComparisonGroup}>{row.groupA}</span>
        <span className={styles.ttestComparisonVs} aria-hidden>
          vs
        </span>
        <span className={styles.ttestComparisonGroup}>{row.groupB}</span>
      </span>
      <span className={styles.ttestComparisonStat}>
        <span className={styles.ttestComparisonStatLabel}>{pLabel}</span>
        <span className={styles.ttestComparisonStatValue}>{pText}</span>
      </span>
      <span
        className={`${styles.significanceMark} ${styles.ttestComparisonSig}`}
        title={pText}
      >
        {sig}
      </span>
    </div>
  );
}

/** 群間比較直下: 群名つき中心傾向・n を一目で（統計詳細へ飛ばずに把握） */
function TTestMeansGlance({
  stats,
  groupA,
  groupB,
  centerLabel,
  showSd,
}: {
  stats: Record<string, unknown> | undefined;
  groupA: string;
  groupB: string;
  /** 「平均」または「中央値」（ノンパラメトリック時は中央値） */
  centerLabel: string;
  /** パラメトリック時は誤差棒（SD）と揃えて ±SD を添える */
  showSd?: boolean;
}) {
  if (!stats) return null;
  /** ノンパラは中央値優先（古い mean* が残っていても誤表示しない） */
  const center1 = showSd
    ? (typeof stats.mean1 === 'number' ? stats.mean1 : stats.median1)
    : (typeof stats.median1 === 'number' ? stats.median1 : stats.mean1);
  const center2 = showSd
    ? (typeof stats.mean2 === 'number' ? stats.mean2 : stats.median2)
    : (typeof stats.median2 === 'number' ? stats.median2 : stats.mean2);
  const n1 = stats.n1;
  const n2 = stats.n2;
  if (typeof center1 !== 'number' || typeof center2 !== 'number') return null;
  const sd1 = typeof stats.stdDev1 === 'number' ? stats.stdDev1 : stats.sd1;
  const sd2 = typeof stats.stdDev2 === 'number' ? stats.stdDev2 : stats.sd2;
  const sd1Text =
    showSd && typeof sd1 === 'number' ? ` ± ${formatStatValue('sd1', sd1)}` : '';
  const sd2Text =
    showSd && typeof sd2 === 'number' ? ` ± ${formatStatValue('sd2', sd2)}` : '';
  const n1Text =
    typeof n1 === 'number' ? ` (n=${formatStatValue('n1', n1)})` : '';
  const n2Text =
    typeof n2 === 'number' ? ` (n=${formatStatValue('n2', n2)})` : '';
  return (
    <p className={styles.meansGlance} data-keisuke-ttest-means-glance>
      <span className={styles.meansGlanceKind}>{centerLabel}</span>{' '}
      <span>
        {groupA}: {formatStatValue('mean1', center1)}
        {sd1Text}
        {n1Text}
      </span>
      <span aria-hidden className={styles.meansGlanceSep}>
        ·
      </span>
      <span>
        {groupB}: {formatStatValue('mean2', center2)}
        {sd2Text}
        {n2Text}
      </span>
    </p>
  );
}

export default function KeisukeDashboardResultTabs({
  fillHeight = false,
}: {
  /** 埋め込みカード内で結果帯が列高を埋めるとき */
  fillHeight?: boolean;
}) {
  const { t } = useTranslation();
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [calcStepsOpen, setCalcStepsOpen] = useState(false);
  const [reportPdfBusy, setReportPdfBusy] = useState(false);
  const [paperCopyFlash, setPaperCopyFlash] = useState<'idle' | 'ok' | 'err'>('idle');
  const paperCopyFlashTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (paperCopyFlashTimerRef.current != null) {
        window.clearTimeout(paperCopyFlashTimerRef.current);
      }
    },
    []
  );

  const flashPaperCopy = useCallback((ok: boolean) => {
    if (paperCopyFlashTimerRef.current != null) {
      window.clearTimeout(paperCopyFlashTimerRef.current);
    }
    setPaperCopyFlash(ok ? 'ok' : 'err');
    paperCopyFlashTimerRef.current = window.setTimeout(() => {
      setPaperCopyFlash('idle');
      paperCopyFlashTimerRef.current = null;
    }, 2000);
  }, []);

  const { activeResultTab, setActiveResultTab, resultCache, analysisOptions, gridData, setAnalysisOptions } =
    useKeisukeDashboardStore(
    useShallow((s) => ({
      activeResultTab: s.activeResultTab,
      setActiveResultTab: s.setActiveResultTab,
      resultCache: s.resultCache,
      analysisOptions: s.analysisOptions,
      gridData: s.gridData,
      setAnalysisOptions: s.setAnalysisOptions,
    }))
  );

  const paperTextLocale = normalizePaperTextLocale(analysisOptions?.paperTextLocale);
  const displayedPaperText = resolvePaperText(resultCache, analysisOptions ?? {}, paperTextLocale);

  const copyPaperText = useCallback(async () => {
    if (!displayedPaperText) return;
    try {
      await navigator.clipboard.writeText(displayedPaperText);
      flashPaperCopy(true);
    } catch {
      flashPaperCopy(false);
    }
  }, [displayedPaperText, flashPaperCopy]);

  const analysisType = String(analysisOptions?.type ?? 't-test');
  const testType = analysisOptions?.testType ?? 'unpaired';
  const ttestInferential = analysisOptions?.ttestInferential ?? 'parametric';
  const equalVariance = Boolean(analysisOptions?.equalVariance);
  const anovaType = String(analysisOptions?.anovaType ?? 'oneWay');
  const postHoc = String(analysisOptions?.postHoc ?? '');
  const tTestDisplayOpts = { testType, ttestInferential, equalVariance };
  const analysisDisplayLabel =
    analysisType === 't-test'
      ? t(tTestMethodShortLabelKey(tTestDisplayOpts))
      : analysisType;

  const tRow = resultCache?.tTestPairwiseRow;
  const anovaRows = resultCache?.anovaPairwise;
  const calibrationEstimates = resultCache?.calibrationEstimates;
  const showTTestComparison = Boolean(tRow);
  const showAnovaPairwise = Boolean(anovaRows && anovaRows.length > 0);
  const showCalibrationEstimates = Boolean(
    calibrationEstimates && calibrationEstimates.length > 0
  );
  const hasComparisonContent =
    showTTestComparison || showAnovaPairwise || showCalibrationEstimates;

  const visibleResultTabs = useMemo(
    () =>
      hasComparisonContent
        ? RESULT_TAB_KEYS
        : RESULT_TAB_KEYS.filter((row) => row.key !== 'comparison'),
    [hasComparisonContent]
  );

  /** 群間比較が空の解析では統計詳細へ寄せる（非表示タブに留まらない） */
  useEffect(() => {
    if (!resultCache) return;
    if (activeResultTab !== 'comparison') return;
    if (hasComparisonContent) return;
    setActiveResultTab('stats');
  }, [resultCache, activeResultTab, hasComparisonContent, setActiveResultTab]);

  const tTestGroupNames =
    tRow?.groupA && tRow?.groupB ? { a: tRow.groupA, b: tRow.groupB } : undefined;

  const partitionedStats = resultCache?.stats
    ? partitionStatEntries(Object.entries(resultCache.stats))
    : null;
  const assumptionHintKey =
    partitionedStats && partitionedStats.assumptions.length > 0
      ? assumptionHintMessageKey(
          partitionedStats.assumptions,
          typeof resultCache?.stats?.method === 'string'
            ? resultCache.stats.method
            : undefined
        )
      : null;

  /** 群間比較に既出の平均・SD・n を除いた t/df/p/CI */
  const comparisonKeyStatRows = useMemo(() => {
    if (!partitionedStats) return [] as Array<[string, unknown]>;
    return filterCoreStatsBeyondGlance(partitionedStats.core, 8);
  }, [partitionedStats]);
  const comparisonAssumptionRows = partitionedStats?.assumptions ?? [];
  const showComparisonExtras =
    comparisonKeyStatRows.length > 0 || comparisonAssumptionRows.length > 0;

  const calculationDetailsProps = {
    analysisType,
    rawResult: resultCache?.rawResult,
    analysisOptions,
    gridData,
    anovaPairwise: resultCache?.anovaPairwise,
    ttestComparisonCount: analysisOptions?.ttestComparisonCount ?? 1,
  };

  const handleFullReportPdf = useCallback(async () => {
    const resultsRoot = document.querySelector<HTMLElement>('[data-keisuke-report-section="results"]');
    const chartRoot = document.querySelector<HTMLElement>('[data-keisuke-report-section="chart"]');
    if (!resultsRoot || !chartRoot) {
      window.alert(
        t('keisuke.dashboard.reportPdfMissingTargets')
      );
      return;
    }
    setReportPdfBusy(true);
    try {
      const tabLabel = getTabLabel(activeResultTab, t);
      await downloadKeisukeDashboardFullReportPdf({
        resultsRoot,
        chartRoot,
        coverTitle: t('keisuke.dashboard.reportPdfCoverTitle'),
        coverSubtitleLines: [
          `${t('keisuke.dashboard.reportPdfAnalysisLine')}: ${analysisDisplayLabel}`,
          `${t('keisuke.dashboard.reportPdfResultsTabLine')}: ${tabLabel}`,
        ],
      });
    } catch (e) {
      console.error(e);
      window.alert(
        t('keisuke.dashboard.reportPdfFailed')
      );
    } finally {
      setReportPdfBusy(false);
    }
  }, [activeResultTab, analysisDisplayLabel, t]);

  const anovaPairwiseTitle =
    analysisType === 'anova' && anovaType === 'twoWay' && postHoc === 'tukey'
      ? t('keisuke.dashboard.groupComparisonTitleTwoWayTukey')
      : analysisType === 'anova' && anovaType === 'twoWay' && postHoc === 'bonferroni'
        ? t('keisuke.dashboard.groupComparisonTitleTwoWayBonferroni')
        : postHoc === 'bonferroni'
          ? t('keisuke.dashboard.anovaPairwiseTitleBonferroni')
          : t('keisuke.dashboard.anovaPairwiseTitle');

  return (
    <div
      className={`${styles.resultArea}${fillHeight ? ` ${styles.resultAreaFill}` : ''}`}
      data-keisuke-report-section="results"
    >
      <div className={styles.resultTabBar}>
        <div
          className={styles.resultTabList}
          role="tablist"
          aria-label={t('keisuke.workspace.results')}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
              return;
            }
            const idx = visibleResultTabs.findIndex((row) => row.key === activeResultTab);
            if (idx < 0) return;
            e.preventDefault();
            let nextIdx = idx;
            if (e.key === 'ArrowRight') nextIdx = (idx + 1) % visibleResultTabs.length;
            else if (e.key === 'ArrowLeft') {
              nextIdx = (idx - 1 + visibleResultTabs.length) % visibleResultTabs.length;
            } else if (e.key === 'Home') nextIdx = 0;
            else nextIdx = visibleResultTabs.length - 1;
            const nextKey = visibleResultTabs[nextIdx]!.key;
            setActiveResultTab(nextKey);
            queueMicrotask(() => {
              document.getElementById(`keisuke-result-tab-${nextKey}`)?.focus();
            });
          }}
        >
          {visibleResultTabs.map(({ key, labelKey }) => {
            const selected = activeResultTab === key;
            const tabId = `keisuke-result-tab-${key}`;
            const panelId = `keisuke-result-panel-${key}`;
            return (
              <button
                type="button"
                key={key}
                id={tabId}
                role="tab"
                className={`${styles.resultTabButton} ${selected ? styles.resultTabButtonActive : ''}`}
                onClick={() => setActiveResultTab(key)}
                aria-selected={selected}
                aria-controls={panelId}
                tabIndex={selected ? 0 : -1}
              >
                {t(labelKey)}
              </button>
            );
          })}
        </div>
        <div className="ms-auto d-flex align-items-center ps-2" data-html2canvas-ignore="true">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="text-nowrap"
            disabled={reportPdfBusy}
            onClick={() => void handleFullReportPdf()}
            title={t('keisuke.dashboard.reportPdfFullTooltip')}
            data-keisuke-report-pdf-full
          >
            {reportPdfBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {t('keisuke.dashboard.reportPdfBusy')}
              </>
            ) : (
              <>
                <i className="fas fa-file-pdf me-1" aria-hidden />
                {t('keisuke.dashboard.reportPdfFull')}
              </>
            )}
          </Button>
        </div>
      </div>
      <div
        className={styles.tabContent}
        data-keisuke-report-tab-body
        role="tabpanel"
        id={`keisuke-result-panel-${activeResultTab}`}
        aria-labelledby={`keisuke-result-tab-${activeResultTab}`}
      >
        {activeResultTab === 'comparison' && (
          <div
            className={`small ${styles.comparisonPanel}${fillHeight ? ` ${styles.comparisonPanelFill}` : ''}`}
          >
            {showTTestComparison && tRow && (
              <div className={styles.comparisonPrimary}>
                <h6 className={`${styles.comparisonHeading} small fw-semibold`}>
                  {t(tTestComparisonTitleKey(tTestDisplayOpts))}
                </h6>
                <TTestComparisonStrip row={tRow} t={t} postHocKind={postHoc} />
                <TTestMeansGlance
                  stats={resultCache?.stats}
                  groupA={tRow.groupA}
                  groupB={tRow.groupB}
                  centerLabel={
                    ttestInferential === 'parametric'
                      ? t('keisuke.dashboard.ttest.mean')
                      : t('keisuke.dashboard.ttest.median')
                  }
                  showSd={ttestInferential === 'parametric'}
                />
              </div>
            )}
            {showAnovaPairwise && anovaRows && (
              <div className={styles.comparisonPrimary}>
                <h6 className={`${styles.comparisonHeading} small fw-semibold`}>
                  {anovaPairwiseTitle}
                </h6>
                <GroupComparisonTable rows={anovaRows} t={t} postHocKind={postHoc} />
              </div>
            )}
            {showCalibrationEstimates && calibrationEstimates && (
              <div className={styles.comparisonPrimary}>
                <h6 className={`${styles.comparisonHeading} small fw-semibold`}>
                  {t('keisuke.calibrationCurve.estimatedConcentration')}
                </h6>
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>{t('keisuke.calibrationCurve.sampleName')}</th>
                      <th>{t('keisuke.calibrationCurve.absorbance')}</th>
                      <th>
                        {t('keisuke.calibrationCurve.estimatedConcentration')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {calibrationEstimates.map((row, idx) => (
                      <tr key={`${row.name}-${idx}`}>
                        <td>{row.name}</td>
                        <td>{row.absorbance}</td>
                        <td>
                          {row.concentration === null
                            ? '—'
                            : row.concentration.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showComparisonExtras ? (
              <div
                className={`${styles.comparisonKeyStats}${fillHeight ? ` ${styles.comparisonKeyStatsFill}` : ''}`}
              >
                <ComparisonKeyValueTable
                  rows={comparisonKeyStatRows}
                  t={t}
                  groupNames={tTestGroupNames}
                />
                {comparisonAssumptionRows.length > 0 ? (
                  <p className={styles.comparisonAssumptionLabel}>
                    {t('keisuke.dashboard.resultStats.assumptionSection')}
                  </p>
                ) : null}
                <ComparisonKeyValueTable
                  rows={comparisonAssumptionRows}
                  t={t}
                  groupNames={tTestGroupNames}
                />
                {assumptionHintKey ? (
                  <p className={styles.assumptionHint}>
                    {t(`keisuke.dashboard.resultStats.${assumptionHintKey}`)}
                  </p>
                ) : null}
                <button
                  type="button"
                  className={styles.comparisonOpenStats}
                  onClick={() => setActiveResultTab('stats')}
                >
                  {t('keisuke.workspace.statsDockPeekLabel')}
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
        )}
        {activeResultTab === 'stats' && (
          <div className={`small ${styles.statsTab}`}>
            {resultCache?.statsDetailText ? (
              <div
                className={`mb-2 text-body-secondary border rounded p-2 bg-light ${styles.statsDetailPre}`}
                data-keisuke-stats-detail-text
              >
                {resultCache.statsDetailText}
              </div>
            ) : null}
            {partitionedStats ? (
              <>
                <table className="table table-sm table-bordered mb-2">
                  <tbody>
                    {partitionedStats.core
                      /* 見出し prose（statsDetailText）と二重になる method 行は出さない */
                      .filter(([k]) => !(k === 'method' && resultCache?.statsDetailText))
                      .map(([k, v]) => (
                      <tr key={k}>
                        <td className="text-muted">
                          {formatStatKeyLabel(k, t, tTestGroupNames)}
                        </td>
                        <td>{formatStatValue(k, v, t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {partitionedStats.assumptions.length > 0 ? (
                  <details className={styles.assumptionDetails}>
                    <summary className={styles.assumptionSummary}>
                      {t('keisuke.dashboard.resultStats.assumptionSection', {
                        defaultValue: 'Assumption checks (reference)',
                      })}
                    </summary>
                    <table className="table table-sm table-bordered mb-2">
                      <tbody>
                        {partitionedStats.assumptions.map(([k, v]) => (
                          <tr key={k}>
                            <td className="text-muted">
                              {formatStatKeyLabel(k, t, tTestGroupNames)}
                            </td>
                            <td>{formatStatValue(k, v, t)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {assumptionHintKey ? (
                      <p className={styles.assumptionHint}>
                        {t(`keisuke.dashboard.resultStats.${assumptionHintKey}`)}
                      </p>
                    ) : null}
                  </details>
                ) : null}
                <Accordion
                  flush
                  className={styles.calculationStepsAccordion}
                  activeKey={calcStepsOpen ? '0' : undefined}
                  onSelect={(key) => setCalcStepsOpen(key === '0')}
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      {t('keisuke.dashboard.calculationDetailsAccordion')}
                    </Accordion.Header>
                    <Accordion.Body className={styles.calculationStepsBody}>
                      {calcStepsOpen ? (
                        <>
                          <div className={styles.calculationStepsToolbar}>
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setShowCalculationModal(true)}
                              data-keisuke-calculation-details-trigger
                              title={t('keisuke.dashboard.tooltips.calculationDetailsReport')}
                              aria-label={t('keisuke.dashboard.calculationDetailsOpenLarge')}
                            >
                              <i className="fas fa-expand me-1" aria-hidden />
                              {t('keisuke.dashboard.calculationDetailsOpenLarge')}
                            </Button>
                          </div>
                          <CalculationDetailsContent {...calculationDetailsProps} compact />
                        </>
                      ) : null}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </>
            ) : !resultCache?.statsDetailText ? (
              <p className="text-muted mb-0">
                {t('keisuke.dashboard.resultPlaceholder.stats')}
              </p>
            ) : null}
          </div>
        )}
        {activeResultTab === 'paper' && (
          <div className={`small ${styles.paperTab}`}>
            {displayedPaperText ? (
              <>
                <div className={styles.paperToolbar}>
                  <label className={styles.paperLocaleLabel}>
                    <span className="text-muted">{t('keisuke.dashboard.paperTextLocaleLabel')}</span>
                    <select
                      className={`form-select form-select-sm ${styles.paperLocaleSelect}`}
                      value={paperTextLocale}
                      onChange={(e) =>
                        setAnalysisOptions(
                          {
                            paperTextLocale: e.target.value as PaperTextLocale,
                          },
                          { runAnalysis: false }
                        )
                      }
                      aria-label={t('keisuke.dashboard.paperTextLocaleLabel')}
                    >
                      {PAPER_TEXT_LOCALES.map((code) => (
                        <option key={code} value={code}>
                          {t(`keisuke.dashboard.paperTextLocaleOption.${code}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => void copyPaperText()}
                  >
                    {paperCopyFlash === 'ok'
                      ? t('keisuke.dashboard.paperTextCopied')
                      : paperCopyFlash === 'err'
                        ? t('keisuke.dashboard.paperTextCopyFailed')
                        : t('keisuke.dashboard.copyPaperText')}
                  </Button>
                </div>
                <pre className={`mb-0 p-2 bg-light rounded ${styles.paperTextPre}`}>
                  {displayedPaperText}
                </pre>
              </>
            ) : (
              <p className="text-muted mb-0">
                {t('keisuke.dashboard.resultPlaceholder.paper')}
              </p>
            )}
          </div>
        )}      </div>
      <CalculationDetailsModal
        show={showCalculationModal}
        onHide={() => setShowCalculationModal(false)}
        {...calculationDetailsProps}
      />
    </div>
  );
}
